import classNames from 'classnames';
import { createContext, useContext, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink } from 'react-router-dom';

import { FlaskIcon } from './controls';
import { NotYet, NotYetInfo, notYet, type NotYetKey } from './not-yet';

/** Where a page's title goes on a phone: the top bar, beside the logo (AppLayout). */
export const PageTitleSlot = createContext<HTMLElement | null>(null);

/**
 * The top of every page: title, one line on what it is for, and its main actions. A subpage links back to its parent.
 * `children` are what the page is worked with from the top: its tabs, search and filters.
 *
 * On a phone the title moves to the top bar, beside the logo, and the actions and `children` stay in a bar stuck to
 * the top while the page scrolls. On a desktop the actions sit beside the title and nothing sticks.
 */
export const PageHeader = ({
  title,
  description,
  actions,
  back,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  back?: { to: string; label: string };
  children?: ReactNode;
}) => {
  const titleSlot = useContext(PageTitleSlot);
  return (
    <>
      {/* data-page-header: what is done here (a search, a filter) is no work to keep (AppUpdate). */}
      <header data-page-header className={classNames('mb-4 md:mb-6', !back && !description && 'hidden md:block')}>
        {back && (
          <Link to={back.to} className="mb-1 inline-flex min-h-11 items-center gap-1 text-sm text-fg-muted hover:text-fg md:mb-2 md:min-h-0">
            <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M10 3L5 8l5 5" />
            </svg>
            {back.label}
          </Link>
        )}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="hidden break-words text-xl font-semibold text-fg-strong md:block md:text-2xl">{title}</h1>
            {description && <p className="break-words text-sm text-fg-muted md:mt-1">{description}</p>}
          </div>
          {actions && <div className="hidden min-w-0 flex-wrap items-center gap-2 md:flex">{actions}</div>}
        </div>
      </header>
      {titleSlot && createPortal(title, titleSlot)}
      {(actions || children) && (
        // On a phone the bar spans the page's width, on the page's own colour, and its parts sit closer together. It
        // sticks at -top-4, the main's own top padding, so nothing shows above it while the page scrolls.
        <div data-page-header className="sticky -top-4 z-20 -mx-4 mb-2 bg-ink px-4 py-2 max-md:[&>*:last-child]:mb-0 max-md:[&>*]:mb-2 md:static md:z-auto md:m-0 md:bg-transparent md:p-0">
          {/* No shrink-0: on a phone the actions wrap inside the width instead of running off it. */}
          {actions && <div className="flex min-w-0 flex-wrap items-center gap-2 md:hidden">{actions}</div>}
          {children}
        </div>
      )}
    </>
  );
};

/** `off`: the tab does not work in this version (NOT_YET): its label shows faded, with the info mark. */
export type SubNavItem = { to: string; label: string; end?: boolean; count?: number; off?: NotYetKey };

/**
 * Tabs between a section's subpages. The current one is marked by colour and an underline, never by weight.
 * The bar scrolls sideways when the tabs do not fit; its line is an inset shadow and the tabs sit inside the
 * bar, so nothing spills over vertically and no vertical scrollbar appears.
 */
export const SubNav = ({ items, label }: { items: SubNavItem[]; label: string }) => (
  <nav aria-label={label} className="mb-6 flex gap-1 overflow-x-auto overflow-y-hidden shadow-[inset_0_-1px_0_var(--line)]">
    {items.map((i) => (
      <NavLink
        key={i.to}
        to={i.to}
        end={i.end}
        className={({ isActive }) =>
          classNames(
            'whitespace-nowrap px-3 py-3 text-sm transition-colors md:py-2',
            isActive ? 'text-brand shadow-[inset_0_-2px_0_theme(colors.brand.DEFAULT)]' : 'text-fg-muted hover:text-fg',
          )
        }
      >
        {i.off ? <span className={notYet}>{i.label}</span> : i.label}
        {i.count !== undefined && <span className="ml-1.5 text-xs text-fg-faint">{i.count}</span>}
        {i.off && <NotYetInfo what={i.off} nested className="ml-1.5 align-[-3px]" />}
      </NavLink>
    ))}
  </nav>
);

/**
 * A container resting on the page (elevation 1), with an optional title row. `notYet`: it
 * does not work in this version, so it shows faded, with the info mark at the end of its
 * title row.
 */
export const Card = ({
  title,
  aside,
  children,
  className,
  notYet: off,
}: {
  title?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
  className?: string;
  notYet?: NotYetKey;
}) => {
  const card = (
    <section className={classNames('rounded-card bg-surface p-4 shadow-e1 md:p-5', className)}>
      {(title || aside) && (
        <div className={classNames('mb-3 flex flex-wrap items-center justify-between gap-2', off && 'pr-7')}>
          {title && <h2 className="min-w-0 break-words text-sm font-medium text-fg-strong">{title}</h2>}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
  return off ? (
    <NotYet what={off} info="right-4 top-[19px] md:right-5 md:top-[23px]">
      {card}
    </NotYet>
  ) : (
    card
  );
};

/** Marks a page that is not connected to data yet: everything on it is made up (CLAUDE.md, "Demo data"). */
export const PreviewBanner = ({ requirements, children }: { requirements?: string[]; children?: ReactNode }) => (
  <div role="note" className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-card bg-layer px-4 py-3 text-sm text-fg-muted">
    <FlaskIcon className="h-4 w-4 shrink-0" />
    <span>
      <span className="text-fg-strong">Preview.</span> {children ?? 'Not connected to data yet: everything on this page is made up.'}
    </span>
    {requirements && requirements.length > 0 && (
      <span className="flex flex-wrap gap-1.5">
        {requirements.map((r) => (
          <span key={r} className="pill rounded-pill px-2 py-0.5 font-mono text-xs text-fg-muted [--tag-color:rgb(var(--c-surface))]">
            {r}
          </span>
        ))}
      </span>
    )}
  </div>
);

// Each tone is the pill's own colour (.pill): its fill, and the glow of its shadow.
const TONES = {
  neutral: 'text-fg-muted',
  brand: 'text-brand [--tag-color:theme(colors.brand.DEFAULT/15%)]',
  ok: 'text-fg [--tag-color:theme(colors.ok/15%)]',
  warn: 'text-fg [--tag-color:theme(colors.warn/20%)]',
  danger: 'text-danger [--tag-color:theme(colors.danger/15%)]',
  info: 'text-fg [--tag-color:theme(colors.info/15%)]',
};

/** A short status label. The word carries the meaning; the colour only supports it. */
export const Badge = ({ tone = 'neutral', children, title }: { tone?: keyof typeof TONES; children: ReactNode; title?: string }) => (
  <span title={title} className={classNames('pill inline-flex items-center whitespace-nowrap rounded-pill px-2 py-0.5 text-xs', TONES[tone])}>
    {children}
  </span>
);

/** One label and value in a definition list (<dl>). */
export const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-[110px_1fr] gap-3 py-1 text-sm md:grid-cols-[140px_1fr]">
    <dt className="text-fg-muted">{label}</dt>
    <dd className="min-w-0 break-words text-fg">{children}</dd>
  </div>
);
